/**
 * Security utility functions for Fox River AI application
 */

/**
 * Sanitizes user input to prevent XSS attacks
 * @param {string} input - User input to sanitize
 * @returns {string} Sanitized input
 */
export const sanitizeInput = (input) => {
  if (!input) return '';
  
  // Convert to string if not already
  const str = String(input);
  
  // Replace potentially dangerous characters
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

/**
 * Validates file size and type
 * @param {File} file - File object to validate
 * @param {number} maxSizeMB - Maximum file size in MB
 * @param {string[]} allowedTypes - Array of allowed MIME types
 * @param {string[]} allowedExtensions - Array of allowed file extensions
 * @returns {Object} Validation result with isValid and message properties
 */
export const validateFile = (file, maxSizeMB = 5, allowedTypes = [], allowedExtensions = []) => {
  if (!file) {
    return { isValid: false, message: 'No file provided' };
  }
  
  // Check file size
  if (file.size > maxSizeMB * 1024 * 1024) {
    return { 
      isValid: false, 
      message: `File is too large. Please upload a file smaller than ${maxSizeMB}MB.` 
    };
  }
  
  // Check file type
  if (allowedTypes.length > 0 && !allowedTypes.includes(file.type)) {
    return { 
      isValid: false, 
      message: `Unsupported file type. Please upload one of the following types: ${allowedTypes.join(', ')}` 
    };
  }
  
  // Check file extension
  if (allowedExtensions.length > 0) {
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (!allowedExtensions.includes(fileExtension)) {
      return { 
        isValid: false, 
        message: `Unsupported file extension. Please upload files with the following extensions: ${allowedExtensions.join(', ')}` 
      };
    }
  }
  
  return { isValid: true, message: 'File is valid' };
};

/**
 * Rate limiting utility to prevent abuse
 * @param {Function} fn - Function to rate limit
 * @param {number} limit - Number of allowed calls
 * @param {number} interval - Time interval in milliseconds
 * @returns {Function} Rate limited function
 */
export const rateLimit = (fn, limit = 5, interval = 60000) => {
  const calls = [];
  
  return function(...args) {
    const now = Date.now();
    
    // Remove expired timestamps
    while (calls.length > 0 && calls[0] < now - interval) {
      calls.shift();
    }
    
    // Check if limit is reached
    if (calls.length >= limit) {
      console.warn('Rate limit exceeded');
      return Promise.reject(new Error('Rate limit exceeded. Please try again later.'));
    }
    
    // Add current timestamp
    calls.push(now);
    
    // Call the original function
    return fn.apply(this, args);
  };
};

/**
 * Generates a Content Security Policy header value
 * @returns {string} CSP header value
 */
export const generateCSP = () => {
  return `
    default-src 'self';
    script-src 'self';
    style-src 'self' 'unsafe-inline' https://fonts.googleapis.com;
    img-src 'self' data: https://*.amazonaws.com;
    font-src 'self' https://fonts.gstatic.com;
    media-src 'self' https://*.amazonaws.com https://foxriverai-static-assets.s3.amazonaws.com;
    connect-src 'self' https://*.amazonaws.com https://*.execute-api.us-east-1.amazonaws.com https://*.amazoncognito.com;
    form-action 'self';
    base-uri 'self';
    object-src 'none';
  `.replace(/\s+/g, ' ').trim();
};